import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { simplifyHtmlTable } from '../utils/htmlTableSimplifier';
import { getSharedInventoryList, getSeriesManagement } from '../services/apiService';
import SharedInventoryCreateModal from './SharedInventoryCreateModal';

// Simple modal for bulk price import (text/CSV/XLSX)
// Props:
// - open, onClose
// - onApply: ({ rows, mode }) where rows = [{ date:'YYYY-MM-DD', adult:number, child:number, infant:number }]
// - onDeleteZero?: ({ rows, mode, zeroRows }) => void  // zeroRows = subset of rows with all zero prices
// - onDeleteZeroExisting?: ({ startDate, endDate }) => void  // delete zero-price days that are already on the server within range
// - defaultMode: 'total' | 'byAge'
const BulkPriceImportModal = ({ open, onClose, onApply, onDeleteZero, defaultMode = 'total', initialText = '', enableSharedStock = false, productNo = null, initialInputTab = 'json' }) => {
  const [inputTab, setInputTab] = useState('json'); // text | json
  const [jsonEditorMounted, setJsonEditorMounted] = useState(false);
  const [jsonEditorHtmlCache, setJsonEditorHtmlCache] = useState('');
  const lastInputTabRef = useRef('json');
  const [mode, setMode] = useState(defaultMode); // 'total' | 'byAge'
  const [text, setText] = useState('');
  const allWeekdays = [0, 1, 2, 3, 4, 5, 6];
  const [weekdaySelection, setWeekdaySelection] = useState(() => new Set(allWeekdays));
  const [rawRows, setRawRows] = useState([]); // unfiltered parsed rows
  const [rows, setRows] = useState([]); // filtered preview
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [animOut, setAnimOut] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [stockCount, setStockCount] = useState('');
  const [showCommission, setShowCommission] = useState(false);
  const [commissionType, setCommissionType] = useState('정률');
  const prevCommissionTypeRef = useRef('정률');
  const [commission, setCommission] = useState({ ma: '', mc: '', mi: '' });
  const [useSharedStock, setUseSharedStock] = useState(false);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedList, setSharedList] = useState([]);
  const [selectedSeriesNo, setSelectedSeriesNo] = useState(null);
  const [sharedModalOpen, setSharedModalOpen] = useState(false);
  const [sharedSelectTemp, setSharedSelectTemp] = useState(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [sharedFilters, setSharedFilters] = useState({ name: '', supplier: '', kind: '' });
  const [sharedCreateOpen, setSharedCreateOpen] = useState(false);
  const [seatStatusLoading, setSeatStatusLoading] = useState(false);
  const [seatStatus, setSeatStatus] = useState([]);
  const [seatStatusMeta, setSeatStatusMeta] = useState({ seriesNo: null, seriesName: '', color: '' });
  const [seatStatusQueried, setSeatStatusQueried] = useState(false);
  const autoParseTimer = useRef(null);

  const iframeRef = useRef(null);
  const [seReady, setSeReady] = useState(false);
  const seGetResolverRef = useRef(null);
  const [convertLoading, setConvertLoading] = useState(false);

  const filteredSharedList = useMemo(() => {
    const name = String(sharedFilters.name || '').toLowerCase();
    const supplier = String(sharedFilters.supplier || '').toLowerCase();
    const kind = String(sharedFilters.kind || '').toLowerCase();
    return (Array.isArray(sharedList) ? sharedList : []).filter(row => {
      const nm = String(row?.시리즈명 || '').toLowerCase();
      const sp = String(row?.공급사 || '').toLowerCase();
      const kd = String(row?.종류 || '').toLowerCase();
      return (!name || nm.includes(name)) && (!supplier || sp.includes(supplier)) && (!kind || kd.includes(kind));
    });
  }, [sharedList, sharedFilters]);

  useEffect(() => {
    if (open) {
      const safeInitialTab = initialInputTab === 'text' ? 'text' : 'json';
      setMounted(true);
      setAnimOut(false);
      setError('');
      setRawRows([]);
      setRows([]);
      setText(String(initialText || ''));
      setInputTab(safeInitialTab);
      setJsonEditorMounted(safeInitialTab === 'json');
      setJsonEditorHtmlCache('');
      lastInputTabRef.current = safeInitialTab;
      setMode(defaultMode);
      setStockCount('');
      setShowCommission(false);
      setCommissionType('정률');
      prevCommissionTypeRef.current = '정률';
      setCommission({ ma: '', mc: '', mi: '' });
      setUseSharedStock(false);
      setSharedLoading(false);
      setSharedList([]);
      setSelectedSeriesNo(null);
      setSharedModalOpen(false);
      setSharedSelectTemp(null);
      setSelectedSeriesName('');
      setSharedFilters({ name: '', supplier: '', kind: '' });
      setSharedCreateOpen(false);
      setSeatStatusLoading(false);
      setSeatStatus([]);
      setSeatStatusMeta({ seriesNo: null, seriesName: '', color: '' });
      setSeatStatusQueried(false);
      setSeReady(false);
      setConvertLoading(false);
      setWeekdaySelection(new Set(allWeekdays));
    }
  }, [open, defaultMode, initialText, initialInputTab]);

  useEffect(() => {
    (async () => {
      try {
        if (!open) return;
        if (!enableSharedStock) return;
        if (!useSharedStock && !sharedModalOpen) return;
        if (!productNo) return;
        setSharedLoading(true);
        const list = await getSharedInventoryList({ productNo: Number(productNo) });
        setSharedList(Array.isArray(list) ? list : []);
      } catch (_) {
        setSharedList([]);
      } finally {
        setSharedLoading(false);
      }
    })();
  }, [open, enableSharedStock, useSharedStock, sharedModalOpen, productNo]);

  useEffect(() => {
    if (!selectedSeriesNo) { setSelectedSeriesName(''); return; }
    const found = (Array.isArray(sharedList) ? sharedList : []).find(row => String(row?.시리즈번호) === String(selectedSeriesNo));
    setSelectedSeriesName(found?.시리즈명 || '');
  }, [selectedSeriesNo, sharedList]);

  useEffect(() => {
    if (!enableSharedStock) return;
    const next = !!selectedSeriesNo;
    setUseSharedStock(next);
    if (next) setStockCount('');
  }, [enableSharedStock, selectedSeriesNo]);

  useEffect(() => {
    if (!open) return;
    const prev = lastInputTabRef.current;
    lastInputTabRef.current = inputTab;

    if (prev === 'json' && inputTab !== 'json') {
      (async () => {
        try {
          const { html } = await getEditorHtml();
          setJsonEditorHtmlCache(String(html || ''));
        } catch (_) {
          // ignore
        } finally {
          setJsonEditorMounted(false);
          setSeReady(false);
        }
      })();
      return;
    }

    if (inputTab === 'json') {
      setJsonEditorMounted(true);
    }
  }, [inputTab, open]);

  useEffect(() => {
    if (!open) return;
    if (inputTab !== 'json') return;
    if (!seReady) return;

    try {
      const w = iframeRef.current?.contentWindow;
      if (!w) return;
      if (jsonEditorHtmlCache) {
        w.postMessage({ type: 'SE_SET', html: jsonEditorHtmlCache }, '*');
      }
      try { w.focus(); } catch (_) { }
      try {
        // SmartEditor recalculates its editing area on resize.
        if (w.Event && typeof w.dispatchEvent === 'function') {
          w.dispatchEvent(new w.Event('resize'));
        }
      } catch (_) { }
    } catch (_) { }
  }, [seReady, inputTab, open, jsonEditorHtmlCache]);

  const requestClose = () => {
    setAnimOut(true);
    setTimeout(() => onClose && onClose(), 200);
  };

  // Auto-parse pasted/typed text with a short debounce
  useEffect(() => {
    if (!mounted) return;
    if (inputTab !== 'text') return;
    if (autoParseTimer.current) {
      clearTimeout(autoParseTimer.current);
      autoParseTimer.current = null;
    }
    // If text is empty, clear preview and error and skip
    if (!String(text || '').trim()) {
      setRawRows([]);
      setRows([]);
      setError('');
      return;
    }
    autoParseTimer.current = setTimeout(() => {
      parsePasted();
    }, 400);
    return () => {
      if (autoParseTimer.current) {
        clearTimeout(autoParseTimer.current);
        autoParseTimer.current = null;
      }
    };
  }, [text, mode, mounted, inputTab]);

  useEffect(() => {
    const handler = (e) => {
      const data = e.data || {};
      if (data?.type === 'SE_READY') setSeReady(true);
      if (data?.type === 'SE_GET_RESULT' && seGetResolverRef.current) {
        const html = data?.html || '';
        seGetResolverRef.current({ html });
        seGetResolverRef.current = null;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const getEditorHtml = () => {
    return new Promise((resolve) => {
      const w = iframeRef.current?.contentWindow;
      if (!w) return resolve({ html: '' });
      seGetResolverRef.current = resolve;
      try {
        w.postMessage({ type: 'SE_GET' }, '*');
      } catch {
        resolve({ html: '' });
      }
      const timer = setTimeout(() => {
        if (seGetResolverRef.current) {
          seGetResolverRef.current = null;
          resolve({ html: '' });
        }
      }, 5000);
      const originalResolver = seGetResolverRef.current;
      seGetResolverRef.current = (payload) => {
        clearTimeout(timer);
        originalResolver && originalResolver(payload);
      };
    });
  };

  const handleConvertFromSmartEditor = async () => {
    if (!seReady) {
      setError('에디터가 아직 준비되지 않았습니다.');
      return;
    }
    setConvertLoading(true);
    setParsing(true);
    setError('');
    try {
      const { html } = await getEditorHtml();
      const sanitizedHtml = String(html || '').replace(/src\s*=\s*"file:[^"]*"/gi, '');
      const simplifiedHtml = simplifyHtmlTable(sanitizedHtml);
      const resp = await fetch('https://revato.mytripbox.co.kr/itinerary/df_cellMerge', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain; charset=UTF-8' },
        body: String(simplifiedHtml || ''),
      });
      if (!resp.ok) throw new Error(`요청 실패 (${resp.status})`);
      const data = await resp.json();

      const sub = (data && data.subData) ? data.subData : null;
      const numMap = {};
      if (sub && typeof sub === 'object') {
        const keys = Object.keys(sub).filter((k) => /^\d+$/.test(String(k)));
        keys.sort((a, b) => Number(a) - Number(b));
        keys.forEach((k) => {
          numMap[String(k)] = String(sub[k] ?? '');
        });
      }

      const extractedJson = JSON.stringify(numMap);
      // SmartEditor-derived data is treated as "합계요금 동일" only.
      const jsonRows = extractRowsFromJsonText(extractedJson, 'total');
      setRawRows(jsonRows);
      setRows(filterRowsByWeekday(jsonRows, weekdaySelection));
      setError(jsonRows.length ? '' : '유효한 JSON 데이터(숫자 키 subData)를 찾지 못했습니다.');
    } catch (e) {
      console.error(e);
      setRawRows([]);
      setRows([]);
      setError('스마트 에디터 전환/파싱 중 오류가 발생했습니다.');
    } finally {
      setParsing(false);
      setConvertLoading(false);
    }
  };

  const cleanNumber = (v) => {
    // allow plain numbers or strings with commas/currency
    const s = String(v ?? '').replace(/[^0-9.-]/g, '');
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const pad2 = (n) => String(n).padStart(2, '0');

  const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const weekdayLabel = ['일', '월', '화', '수', '목', '금', '토'];
  const formatDateWithWeekday = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    if (isNaN(d)) return dateStr;
    return `${dateStr} (${weekdayLabel[d.getDay()]})`;
  };

  const filterRowsByWeekday = (list, weekdaySet) => {
    if (!Array.isArray(list)) return [];
    if (!weekdaySet || !(weekdaySet instanceof Set) || weekdaySet.size === 0) return [];
    return list.filter((r) => {
      const d = new Date(`${r.date}T00:00:00`);
      if (isNaN(d)) return false;
      return weekdaySet.has(d.getDay());
    });
  };

  const toggleWeekday = (day) => {
    setWeekdaySelection((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  useEffect(() => {
    setRows(filterRowsByWeekday(rawRows, weekdaySelection));
  }, [rawRows, weekdaySelection]);

  const normalizeNumberInput = (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const formatInput = (s) => {
    const digits = String(s || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    const n = Number(digits);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString();
  };

  const formatPercent = (s) => {
    const digits = String(s || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    let n = Number(digits);
    if (!Number.isFinite(n)) return '';
    if (n > 100) n = 100;
    if (n < 0) n = 0;
    return String(n);
  };

  const formatCommission = (s) => {
    if (commissionType === '정률') return formatPercent(s);
    return formatInput(s);
  };

  useEffect(() => {
    if (!open) return;
    if (prevCommissionTypeRef.current !== commissionType) {
      setCommission({ ma: '', mc: '', mi: '' });
      prevCommissionTypeRef.current = commissionType;
    }
  }, [commissionType, open]);

  const formatNumberDisplay = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString();
  };

  const updateRowValue = (dateKey, field, value) => {
    const n = normalizeNumberInput(value);
    setRawRows((prev) => prev.map((r) => (r?.date === dateKey ? { ...r, [field]: n } : r)));
  };

  const removeRow = (dateKey) => {
    setRawRows((prev) => prev.filter((r) => r?.date !== dateKey));
  };

  const parseDateTokenToYmd = (token) => {
    const s = String(token ?? '').trim();
    if (!s) return null;

    const mKr = s.match(/(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
    if (mKr) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const mm = Number(mKr[1]);
      const dd = Number(mKr[2]);
      if (!Number.isFinite(mm) || !Number.isFinite(dd)) return null;

      let yy = today.getFullYear();
      let d = new Date(yy, mm - 1, dd);
      if (isNaN(d)) return null;
      if (d < today) {
        yy = yy + 1;
        d = new Date(yy, mm - 1, dd);
        if (isNaN(d)) return null;
      }
      return toYmd(d);
    }

    const mYmd = s.match(/(\d{4})\s*[\.\-/]\s*(\d{1,2})\s*[\.\-/]\s*(\d{1,2})/);
    if (mYmd) {
      const yy = Number(mYmd[1]);
      const mm = Number(mYmd[2]);
      const dd = Number(mYmd[3]);
      if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
      const d = new Date(yy, mm - 1, dd);
      if (isNaN(d)) return null;
      return toYmd(d);
    }

    const mMd = s.match(/\b(\d{1,2})\s*[\/.\-]\s*(\d{1,2})\b/);
    if (mMd) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const mm = Number(mMd[1]);
      const dd = Number(mMd[2]);
      if (!Number.isFinite(mm) || !Number.isFinite(dd)) return null;

      let yy = today.getFullYear();
      let d = new Date(yy, mm - 1, dd);
      if (isNaN(d)) return null;
      if (d < today) {
        yy = yy + 1;
        d = new Date(yy, mm - 1, dd);
        if (isNaN(d)) return null;
      }
      return toYmd(d);
    }

    return null;
  };

  const extractDatesFromToken = (token) => {
    const s = String(token ?? '').trim();
    if (!s) return [];

    const weekdayMap = { '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6 };

    const parseWeekdaySet = (line) => {
      const t = String(line ?? '');
      const m = t.match(/\(([^)]*)\)/);
      if (!m) return null;
      const chars = (m[1].match(/[일월화수목금토]/g) || []).filter(Boolean);
      if (!chars.length) return null;
      const set = new Set();
      for (const c of chars) {
        const idx = weekdayMap[c];
        if (Number.isFinite(idx)) set.add(idx);
      }
      return set.size ? set : null;
    };

    const krMdToDate = (mm, dd) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let yy = today.getFullYear();
      let d = new Date(yy, mm - 1, dd);
      if (isNaN(d)) return null;
      if (d < today) {
        yy = yy + 1;
        d = new Date(yy, mm - 1, dd);
        if (isNaN(d)) return null;
      }
      return d;
    };

    const expandRange = (startDate, endMm, endDd) => {
      if (!startDate) return [];
      const endMonth = Number(endMm);
      const endDay = Number(endDd);
      if (!Number.isFinite(endMonth) || !Number.isFinite(endDay)) return [];
      let end = new Date(startDate.getFullYear(), endMonth - 1, endDay);
      if (isNaN(end)) return [];
      if (end < startDate) {
        end = new Date(startDate.getFullYear() + 1, endMonth - 1, endDay);
        if (isNaN(end)) return [];
      }
      const out = [];
      const cur = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      let guard = 0;
      while (cur <= end && guard < 400) {
        out.push(toYmd(cur));
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
      return out;
    };

    const lines = s.split(/\r?\n/).map(x => String(x ?? '').trim()).filter(Boolean);
    const out = [];
    let pending = null;

    const commitPending = (weekdaySet) => {
      if (!pending || !pending.length) return;
      if (weekdaySet && weekdaySet.size) {
        for (const d of pending) {
          const dt = new Date(`${d}T00:00:00`);
          if (!isNaN(dt) && weekdaySet.has(dt.getDay())) out.push(d);
        }
      } else {
        pending.forEach(d => out.push(d));
      }
      pending = null;
    };

    for (const line of lines) {
      const weekdaySet = parseWeekdaySet(line);
      if (weekdaySet && pending) {
        commitPending(weekdaySet);
        continue;
      }

      if (pending) commitPending(null);

      const norm = line.replace(/[–—]/g, '-');
      // Support ranges without the explicit "일" suffix, e.g. "9월 1~30" or "12월 01~09"
      const mRangeKrNoIl = norm.match(/(\d{1,2})\s*월\s*(\d{1,2})\s*[-~]\s*(?:(\d{1,2})\s*월\s*)?(\d{1,2})\b/);
      if (mRangeKrNoIl) {
        const sm = Number(mRangeKrNoIl[1]);
        const sd = Number(mRangeKrNoIl[2]);
        const em = Number(mRangeKrNoIl[3] || mRangeKrNoIl[1]);
        const ed = Number(mRangeKrNoIl[4]);
        const start = krMdToDate(sm, sd);
        const expanded = expandRange(start, em, ed);
        if (expanded.length) {
          pending = expanded;
          continue;
        }
      }
      const mRangeKr = norm.match(/(\d{1,2})\s*월\s*(\d{1,2})\s*일\s*[-~]\s*(?:(\d{1,2})\s*월\s*)?(\d{1,2})\s*일/);
      if (mRangeKr) {
        const sm = Number(mRangeKr[1]);
        const sd = Number(mRangeKr[2]);
        const em = Number(mRangeKr[3] || mRangeKr[1]);
        const ed = Number(mRangeKr[4]);
        const start = krMdToDate(sm, sd);
        const expanded = expandRange(start, em, ed);
        if (expanded.length) {
          pending = expanded;
          continue;
        }
      }

      // Support slash ranges like "7/20~8/12" or "9/1~30" (end month can be omitted)
      const mRangeSlash = norm.match(/(\d{1,2})\s*\/\s*(\d{1,2})\s*[-~]\s*(?:(\d{1,2})\s*\/\s*)?(\d{1,2})\b/);
      if (mRangeSlash) {
        const sm = Number(mRangeSlash[1]);
        const sd = Number(mRangeSlash[2]);
        const em = Number(mRangeSlash[3] || mRangeSlash[1]);
        const ed = Number(mRangeSlash[4]);
        const start = krMdToDate(sm, sd);
        const expanded = expandRange(start, em, ed);
        if (expanded.length) {
          pending = expanded;
          continue;
        }
      }

      // Support multi-month list with slash separation, e.g. "9월 30일 / 10월 7, 14, 21, 28일"
      const looksLikeMultiMonthList = norm.includes('/') && (norm.match(/월/g) || []).length >= 2;
      if (looksLikeMultiMonthList) {
        const pieces = norm.split('/').map(p => String(p || '').trim()).filter(Boolean);
        const found = [];
        for (const seg of pieces) {
          const mmMatch = seg.match(/(\d{1,2})\s*월/);
          if (!mmMatch) continue;
          const mm = Number(mmMatch[1]);
          if (!Number.isFinite(mm)) continue;
          const dayPart = seg.slice(mmMatch.index + mmMatch[0].length);
          const dayTokens = (dayPart.match(/\d{1,2}/g) || []).map(x => Number(x)).filter(Number.isFinite);
          for (const dd of dayTokens) {
            const y = parseDateTokenToYmd(`${mm}/${dd}`);
            if (y) found.push(y);
          }
        }
        if (found.length) {
          found.forEach(d => out.push(d));
          continue;
        }
      }

      // Support month + day list forms like "9월 / 3, 10, 17, 24"
      const mMonthList = norm.match(/(\d{1,2})\s*월\s*\/\s*(.+)/);
      if (mMonthList) {
        const mm = Number(mMonthList[1]);
        if (Number.isFinite(mm)) {
          const rest = String(mMonthList[2] ?? '');
          const dayTokens = rest
            .replace(/[^0-9,\s]/g, ' ')
            .split(/[\s,]+/)
            .map(x => String(x ?? '').trim())
            .filter(Boolean);
          const found = [];
          for (const dTok of dayTokens) {
            const dd = Number(dTok);
            if (!Number.isFinite(dd)) continue;
            const y = parseDateTokenToYmd(`${mm}/${dd}`);
            if (y) found.push(y);
          }
          if (found.length) {
            found.forEach(d => out.push(d));
            continue;
          }
        }
      }

      // Support compact date lists with month carryover, e.g. "7/19,21,22" or "8/2,5,6"
      // Rule: when a MM/DD token appears, set currentMonth=MM; subsequent DD-only tokens use that month.
      const tokens = String(line)
        .replace(/[–—]/g, '-')
        .split(/[\s,]+/)
        .map(t => String(t ?? '').replace(/[^0-9/]/g, '').trim())
        .filter(Boolean);
      let currentMonth = null;
      const found = [];
      for (const t of tokens) {
        const mmd = t.match(/^(\d{1,2})\/(\d{1,2})$/);
        if (mmd) {
          const mm = Number(mmd[1]);
          const dd = Number(mmd[2]);
          if (Number.isFinite(mm) && Number.isFinite(dd)) {
            currentMonth = mm;
            const y = parseDateTokenToYmd(`${mm}/${dd}`);
            if (y) found.push(y);
          }
          continue;
        }
        const ddOnly = t.match(/^(\d{1,2})$/);
        if (ddOnly && currentMonth != null) {
          const dd = Number(ddOnly[1]);
          if (Number.isFinite(dd)) {
            const y = parseDateTokenToYmd(`${currentMonth}/${dd}`);
            if (y) found.push(y);
          }
        }
      }
      if (found.length) {
        found.forEach(d => out.push(d));
        continue;
      }

      const single = parseDateTokenToYmd(line);
      if (single) {
        out.push(single);
        continue;
      }
    }

    if (pending) commitPending(null);

    if (!out.length) return out;
    const uniq = Array.from(new Set(out));
    return uniq.sort((a, b) => String(a).localeCompare(String(b)));
  };

  const stripHtml = (s) => {
    return String(s ?? '')
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/[\u00A0]/g, ' ');
  };

  const stripDateSubstrings = (s) => {
    return String(s ?? '')
      .replace(/\b\d{4}\s*[\.\-/]\s*\d{1,2}\s*[\.\-/]\s*\d{1,2}\b/g, ' ')
      .replace(/\b\d{1,2}\s*[\/.\-]\s*\d{1,2}\b/g, ' ')
      .replace(/\b\d{1,2}\s*월\s*\d{1,2}\s*일\b/g, ' ');
  };

  const extractPriceFromToken = (token) => {
    const s = String(token ?? '');
    // Be strict to avoid treating dates like "10월 03일" as price (=10).
    // Priority:
    // 1) number with explicit currency marker
    // 2) number with thousands separators
    // 3) plain digits with length >= 4
    const mCurrency = s.match(/([0-9][0-9,]*)\s*(?:원|KRW|₩)/);
    if (mCurrency) {
      const n = cleanNumber(mCurrency[1]);
      return Number.isFinite(n) ? n : null;
    }
    const mComma = s.match(/(\d{1,3}(?:,\d{3})+)/);
    if (mComma) {
      const n = cleanNumber(mComma[1]);
      return Number.isFinite(n) ? n : null;
    }
    const mPlain = s.match(/\b(\d{4,})\b/);
    if (mPlain) {
      const n = cleanNumber(mPlain[1]);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const extractRowsFromJsonText = (whole, mode) => {
    const txt = String(whole ?? '').trim();
    if (!txt) return [];
    const looksLikeJson = (txt.startsWith('{') || txt.startsWith('['));
    const looksLikeJsonFragment = !looksLikeJson && /"\s*[^\"]+\s*"\s*:/.test(txt);
    if (!looksLikeJson && !looksLikeJsonFragment) return [];

    let parsed;
    try {
      const candidate = looksLikeJson ? txt : `{${txt.replace(/,\s*$/, '')}}`;
      parsed = JSON.parse(candidate);
    } catch (_) {
      return [];
    }

    const extractRowsByMonthGroupedLines = (root) => {
      if (!root || Array.isArray(root) || typeof root !== 'object') return [];
      const entries = Object.entries(root).filter(([, v]) => typeof v === 'string');
      if (entries.length < 2) return [];

      const isNumericKey = (k) => /^\d+$/.test(String(k));
      entries.sort((a, b) => {
        const ak = String(a[0]);
        const bk = String(b[0]);
        const an = isNumericKey(ak);
        const bn = isNumericKey(bk);
        if (an && bn) return Number(ak) - Number(bk);
        if (an && !bn) return -1;
        if (!an && bn) return 1;
        return ak.localeCompare(bk);
      });

      const now = new Date();
      const todayYear = now.getFullYear();
      const todayMonth = now.getMonth() + 1;

      let currentMonth = null;
      let currentYear = null;
      let lastMonth = null;

      const byDate = new Map();

      const buildDatesFromDays = (mm, yy, dateCell) => {
        if (!Number.isFinite(mm) || !Number.isFinite(yy)) return [];
        if (mm < 1 || mm > 12) return [];

        const norm = String(dateCell ?? '').replace(/[–—]/g, '-');
        const days = new Set();

        const rangeRe = /(\d{1,2})\s*[-~]\s*(\d{1,2})/g;
        let m;
        while ((m = rangeRe.exec(norm)) !== null) {
          const s = Number(m[1]);
          const e = Number(m[2]);
          if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
          if (s < 1 || s > 31 || e < 1 || e > 31) continue;
          const from = Math.min(s, e);
          const to = Math.max(s, e);
          for (let d = from; d <= to; d++) days.add(d);
        }

        const nums = (norm.match(/\d{1,2}/g) || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n >= 1 && n <= 31);
        nums.forEach((n) => days.add(n));

        const out = [];
        for (const dd of Array.from(days)) {
          const d = new Date(yy, mm - 1, dd);
          if (isNaN(d)) continue;
          if (d.getMonth() !== (mm - 1) || d.getDate() !== dd) continue;
          out.push(toYmd(d));
        }
        if (!out.length) return out;
        return Array.from(new Set(out)).sort((a, b) => String(a).localeCompare(String(b)));
      };

      for (const [, raw] of entries) {
        const cleaned = stripHtml(raw);
        if (!cleaned.includes('||')) continue;

        const cells = cleaned.split('||').map((x) => String(x ?? '').trim());
        if (cells.length < 3) continue;

        const monthCell = String(cells[0] ?? '').trim();
        const mmMatch = monthCell.match(/(\d{1,2})\s*월/);
        if (mmMatch) {
          const mm = Number(mmMatch[1]);
          if (Number.isFinite(mm) && mm >= 1 && mm <= 12) {
            if (currentYear == null) {
              currentYear = (mm < todayMonth) ? (todayYear + 1) : todayYear;
            } else if (lastMonth != null && mm < lastMonth) {
              currentYear = currentYear + 1;
            }
            currentMonth = mm;
            lastMonth = mm;
          }
        }

        let price = null;
        let priceIndex = -1;
        for (let i = cells.length - 1; i >= 0; i--) {
          const p = extractPriceFromToken(cells[i]);
          if (p != null) {
            price = p;
            priceIndex = i;
            break;
          }
        }
        if (price == null || priceIndex <= 0) continue;

        let dateCell = '';
        for (let i = priceIndex - 1; i >= 0; i--) {
          const candidate = String(cells[i] ?? '').trim();
          if (!candidate) continue;
          if (/\d/.test(candidate) || candidate.includes('/') || /\d{1,2}\s*월/.test(candidate)) {
            dateCell = candidate;
            break;
          }
        }
        if (!dateCell) continue;

        let dates = extractDatesFromToken(dateCell);
        if (!dates.length && currentMonth != null && currentYear != null) {
          dates = buildDatesFromDays(currentMonth, currentYear, dateCell);
        }
        if (!dates.length) continue;

        const span = dates.length;
        for (const d of dates) {
          const row = (mode === 'total')
            ? { date: d, adult: price, child: price, infant: price, _span: span }
            : { date: d, adult: price, child: 0, infant: 0, _span: span };
          const prev = byDate.get(d);
          const prevSpan = (prev && Number.isFinite(Number(prev._span)))
            ? Number(prev._span)
            : Number.POSITIVE_INFINITY;
          if (!prev || span < prevSpan || span === prevSpan) {
            byDate.set(d, row);
          }
        }
      }

      const out = Array.from(byDate.values());
      if (!out.length) return out;
      return out.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    };

    const extractRowsByAlignedColumns = (root) => {
      if (!root || Array.isArray(root) || typeof root !== 'object') return [];
      const rowStrings = Object.values(root).filter(v => typeof v === 'string');
      if (rowStrings.length < 2) return [];

      const table = rowStrings
        .map(v => stripHtml(v))
        .map(v => v.split('||').map(x => String(x ?? '').trim()));

      const scoreDateRow = (cells) => {
        let c = 0;
        for (let i = 0; i < cells.length; i++) if (extractDatesFromToken(cells[i]).length) c++;
        return c;
      };
      const scorePriceRow = (cells) => {
        let c = 0;
        for (let i = 0; i < cells.length; i++) if (extractPriceFromToken(cells[i]) != null) c++;
        return c;
      };

      let dateRow = null;
      let priceRow = null;
      let bestDate = 0;
      let bestPrice = 0;
      for (const cells of table) {
        const ds = scoreDateRow(cells);
        const ps = scorePriceRow(cells);
        if (ds > bestDate) { bestDate = ds; dateRow = cells; }
        if (ps > bestPrice) { bestPrice = ps; priceRow = cells; }
      }
      if (!dateRow || !priceRow) return [];
      if (bestDate === 0 || bestPrice === 0) return [];

      const isHeaderCell = (cell) => {
        const s = String(cell ?? '').trim();
        if (!s) return true;
        if (extractDatesFromToken(s).length) return false;
        if (extractPriceFromToken(s) != null) return false;
        // label-like: contains letters/hangul and no date/price
        return /[A-Za-z가-힣]/.test(s);
      };
      const startIndex = (isHeaderCell(dateRow[0]) && isHeaderCell(priceRow[0])) ? 1 : 0;
      // Try to find the first column that looks like a price to use as a fallback
      const fallbackPriceIndex = (() => {
        const cols = Math.max(dateRow.length, priceRow.length);
        for (let i = startIndex; i < cols; i++) {
          const cell = priceRow[i];
          if (cell != null && extractPriceFromToken(cell) != null) return i;
        }
        return startIndex;
      })();

      const out = [];
      const cols = Math.max(dateRow.length, priceRow.length);
      for (let i = startIndex; i < cols; i++) {
        const dates = extractDatesFromToken(dateRow[i]);
        let price = null;
        if (priceRow[i] != null && String(priceRow[i]).trim()) {
          price = extractPriceFromToken(priceRow[i]);
        }
        if (price == null) {
          const fallbackCell = priceRow[fallbackPriceIndex];
          price = extractPriceFromToken(fallbackCell);
        }
        if (!dates.length || price == null) continue;
        const span = dates.length;
        for (const date of dates) {
          if (mode === 'total') out.push({ date, adult: price, child: price, infant: price, _span: span });
          else out.push({ date, adult: price, child: 0, infant: 0, _span: span });
        }
      }

      if (!out.length) return out;
      const byDate = new Map();
      for (const r of out) {
        const prev = byDate.get(r.date);
        const span = Number(r?._span);
        const prevSpan = prev ? (Number.isFinite(Number(prev?._span)) ? Number(prev._span) : Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
        if (!prev || span < prevSpan || span === prevSpan) byDate.set(r.date, r);
      }
      return Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    };

    const monthGrouped = extractRowsByMonthGroupedLines(parsed);
    const aligned = extractRowsByAlignedColumns(parsed);

    const stringValues = [];
    const walk = (node) => {
      if (node == null) return;
      if (typeof node === 'string') {
        stringValues.push(node);
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (typeof node === 'object') {
        Object.values(node).forEach(walk);
      }
    };
    walk(parsed);

    const fallbackOut = [];
    for (const raw of stringValues) {
      const cleaned = stripHtml(raw);
      const cells = cleaned
        .split('||')
        .flatMap(x => String(x ?? '').split(/\r?\n/))
        .map(x => String(x ?? '').trim())
        .filter(Boolean);

      let pendingDates = new Map();
      for (const cell of cells) {
        const dates = extractDatesFromToken(cell);
        if (dates.length) {
          // Accumulate dates across multiple cells/lines until a price token is encountered
          const span = dates.length;
          for (const d of dates) {
            const prevSpan = pendingDates.has(d) ? Number(pendingDates.get(d)) : Number.POSITIVE_INFINITY;
            if (span < prevSpan || span === prevSpan) pendingDates.set(d, span);
          }
          const priceInSame = extractPriceFromToken(stripDateSubstrings(cell));
          if (priceInSame != null) {
            const v = priceInSame;
            for (const [d, sp] of pendingDates.entries()) {
              if (mode === 'total') fallbackOut.push({ date: d, adult: v, child: v, infant: v, _span: Number(sp) });
              else fallbackOut.push({ date: d, adult: v, child: 0, infant: 0, _span: Number(sp) });
            }
            pendingDates = new Map();
          }
          continue;
        }

        const price = extractPriceFromToken(cell);
        if (price != null && pendingDates.size) {
          const v = price;
          for (const [d, sp] of pendingDates.entries()) {
            if (mode === 'total') fallbackOut.push({ date: d, adult: v, child: v, infant: v, _span: Number(sp) });
            else fallbackOut.push({ date: d, adult: v, child: 0, infant: 0, _span: Number(sp) });
          }
          pendingDates = new Map();
        }
      }
    }

    // Merge aligned + fallback to be resilient to misaligned tables
    const merged = [];
    const bestByDate = new Map();
    const consider = (arr) => {
      for (const r of (Array.isArray(arr) ? arr : [])) {
        const dateKey = String(r?.date || '').trim();
        if (!dateKey) continue;
        const span = Number.isFinite(Number(r?._span)) ? Number(r._span) : Number.POSITIVE_INFINITY;
        const prev = bestByDate.get(dateKey);
        const prevSpan = prev ? (Number.isFinite(Number(prev?._span)) ? Number(prev._span) : Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
        if (!prev || span < prevSpan || span === prevSpan) {
          bestByDate.set(dateKey, r);
        }
      }
    };
    consider(monthGrouped);
    consider(aligned);
    consider(fallbackOut);

    for (const r of bestByDate.values()) {
      if (!r) continue;
      const { _span, ...rest } = r;
      merged.push(rest);
    }

    if (!merged.length) return merged;
    return merged.sort((a, b) => String(a.date).localeCompare(String(b.date)));
  };

  // Extract all "YYYY-MM-DD : number(원)" pairs from an arbitrary blob of text.
  // This is robust to comma separators between pairs and newlines.
  const extractPairsFromWhole = (whole, mode) => {
    const out = [];
    if (!whole) return out;
    // Strict numeric capture to prevent greedy consumption across pairs.
    // - allow ascii/fullwidth colon, optional spaces
    // - capture number tokens with optional thousands separators
    // - stop at currency markers if present
    const rePair = /(\d{4}-\d{2}-\d{2})\s*[：:]\s*([0-9][0-9,\.]*)(?:\s*(?:원|KRW|₩))?/g;
    let m;
    while ((m = rePair.exec(whole)) !== null) {
      const date = (m[1] || '').trim();
      const rawVal = (m[2] || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const val = cleanNumber(rawVal);
      if (mode === 'total') {
        out.push({ date, adult: val, child: val, infant: val });
      } else {
        out.push({ date, adult: val, child: 0, infant: 0 });
      }
    }
    return out;
  };

  const parsePasted = () => {
    setParsing(true);
    try {
      const jsonRows = extractRowsFromJsonText(text, mode);
      if (jsonRows.length) {
        setRawRows(jsonRows);
        setRows(filterRowsByWeekday(jsonRows, weekdaySelection));
        setError('');
        return;
      }
      const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const out = [];
      const dateRe = /\b\d{4}-\d{2}-\d{2}\b/g;
      for (const line of lines) {
        // support formats:
        // YYYY-MM-DD: 10000
        // YYYY-MM-DD, 10000, 8000, 0
        // Guard: if a single line contains multiple dates, skip colon parsing to avoid swallowing the rest
        const datesInLine = (line.match(dateRe) || []);
        const byColon = line.split(':');
        if (datesInLine.length <= 1 && byColon.length >= 2 && mode === 'total') {
          const left = byColon[0].trim();
          const date = left.match(/^\d{4}-\d{2}-\d{2}$/) ? left : left.split(/[\s,，、]+/)[0];
          const right = byColon.slice(1).join(':');
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const val = cleanNumber(right);
            out.push({ date, adult: val, child: val, infant: val });
            continue;
          }
        }
        // CSV-like
        const parts = line.split(/[\t,]/).map(s => s.trim());
        if (parts.length >= 2) {
          const date = parts[0];
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            if (mode === 'total') {
              const v = cleanNumber(parts[1] ?? 0);
              out.push({ date, adult: v, child: v, infant: v });
            } else {
              const a = cleanNumber(parts[1] ?? 0);
              const c = cleanNumber(parts[2] ?? 0);
              const i = cleanNumber(parts[3] ?? 0);
              out.push({ date, adult: a, child: c, infant: i });
            }
            continue;
          }
        }
        // If line didn't match, ignore silently
      }
      // Always run a whole-text extraction pass to catch multiple pairs per line
      const whole = String(text || '');
      const pairs = extractPairsFromWhole(whole, mode);
      if (pairs.length) {
        // Merge while deduping identical date+values to avoid double entries
        const seen = new Set(out.map(r => `${r.date}|${r.adult}|${r.child}|${r.infant}`));
        for (const r of pairs) {
          const key = `${r.date}|${r.adult}|${r.child}|${r.infant}`;
          if (!seen.has(key)) {
            out.push(r);
            seen.add(key);
          }
        }
      } else if (out.length === 0) {
        // Fallback: scan by date anchors and slice until next date
        const dateGlobal = /\d{4}-\d{2}-\d{2}/g;
        const anchors = [];
        let dm;
        while ((dm = dateGlobal.exec(whole)) !== null) {
          anchors.push({ index: dm.index, date: dm[0] });
        }
        for (let i = 0; i < anchors.length; i++) {
          const cur = anchors[i];
          const next = anchors[i + 1];
          const slice = whole.slice(cur.index, next ? next.index : undefined);
          // Find the first numeric cluster after colon-like separators
          const afterSep = slice.split(/[：:]/).slice(1).join(':');
          const numMatch = afterSep && afterSep.match(/([0-9][0-9,\.]*)/);
          if (!numMatch) continue;
          const val = cleanNumber(numMatch[1]);
          if (mode === 'total') {
            out.push({ date: cur.date, adult: val, child: val, infant: val });
          } else {
            out.push({ date: cur.date, adult: val, child: 0, infant: 0 });
          }
        }
      }
      setRawRows(out);
      setRows(filterRowsByWeekday(out, weekdaySelection));
      setError(out.length ? '' : '유효한 데이터가 없습니다. 형식: YYYY-MM-DD: 10000 / YYYY-MM-DD, 10000, 8000, 0 / 2025-09-23 : 389,900원 , 2025-09-24 : 489,900원');
    } catch (e) {
      setError('텍스트 파싱 중 오류가 발생했습니다.');
    } finally {
      setParsing(false);
    }
  };

  const handleApply = () => {
    if (!rows || rows.length === 0) {
      setError('적용할 데이터가 없습니다.');
      return;
    }
    const sharedSelected = useSharedStock
      ? (Array.isArray(sharedList) ? sharedList : []).find(r => String(r?.시리즈번호) === String(selectedSeriesNo))
      : null;
    onApply && onApply({
      rows,
      mode,
      stockCount,
      useSharedStock: !!useSharedStock,
      sharedSeriesNo: sharedSelected ? String(sharedSelected?.시리즈번호) : null,
      sharedSeries: sharedSelected || null,
      showCommission: !!showCommission,
      commissionType,
      ma: commission.ma,
      mc: commission.mc,
      mi: commission.mi,
    });
  };

  const handleDeleteZero = () => {
    if (!rows || rows.length === 0) {
      setError('삭제할 데이터가 없습니다.');
      return;
    }
    const zeroRows = rows.filter(r => {
      const a = Number(r.adult || 0);
      const c = Number(r.child || 0);
      const i = Number(r.infant || 0);
      if (mode === 'total') return a === 0; // total mode mirrors to all ages
      return a === 0 && c === 0 && i === 0;
    });
    if (!zeroRows.length) {
      setError('0원 데이터가 없습니다.');
      return;
    }
    onDeleteZero && onDeleteZero({ rows, mode, zeroRows });
  };

  const handleCloseSharedModal = () => {
    setSharedModalOpen(false);
    setSharedSelectTemp(null);
    if (!selectedSeriesNo) {
      setUseSharedStock(false);
      setSelectedSeriesName('');
    }
  };

  const querySeatStatusForSeries = async (seriesNo, seriesName, color) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      // 조회일: 오늘, 종료일: 조회일로부터 1년 뒤
      const today = new Date();
      const oneYearLater = new Date(today);
      oneYearLater.setFullYear(today.getFullYear() + 1);
      const sDate = today;
      const eDate = oneYearLater;
      const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const startDate = toYmd(sDate);
      const endDate = toYmd(eDate);
      setSeatStatusLoading(true);
      setSeatStatus([]);
      setSeatStatusQueried(true);
      setSeatStatusMeta({ seriesNo: seriesNo ?? null, seriesName: seriesName ?? '', color: color ?? '' });
      const list = await getSeriesManagement({
        startDate,
        endDate,
        searchCriteria: '출발일',
        seriesNo: Number(seriesNo),
      });
      setSeatStatus(Array.isArray(list) ? list : []);
    } catch (_) {
      setSeatStatus([]);
    } finally {
      setSeatStatusLoading(false);
    }
  };

  if (!open) return null;

  const sharedCreateModal = (!sharedCreateOpen ? null : (
    <SharedInventoryCreateModal
      open={sharedCreateOpen}
      onClose={() => setSharedCreateOpen(false)}
      initialSeriesType="347001"
      onCreated={(created) => {
        setSharedCreateOpen(false);
        (async () => {
          try {
            if (!productNo) return;
            setSharedLoading(true);
            const list = await getSharedInventoryList({ productNo: Number(productNo) });
            setSharedList(Array.isArray(list) ? list : []);
            if (created && created.seriesNo) {
              const seriesNoStr = String(created.seriesNo);
              setSharedSelectTemp(seriesNoStr);
              const target = (Array.isArray(list) ? list : []).find(r => String(r?.시리즈번호) === seriesNoStr);
              await querySeatStatusForSeries(seriesNoStr, target?.시리즈명 || '', target?.공유색상 || '');
            }
          } catch (_) {
            setSharedList([]);
          } finally {
            setSharedLoading(false);
          }
        })();
      }}
    />
  ));

  const sharedModal = (!sharedModalOpen || typeof document === 'undefined' || !document.body ? null : createPortal(
    <div className="fixed inset-0 z-[100]" onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseSharedModal(); }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">공유재고 선택</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setSharedCreateOpen(true)}
              >
                공유재고 추가
              </button>
              <button className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50" onClick={handleCloseSharedModal}>닫기</button>
            </div>
          </div>
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">시리즈명</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.name} onChange={(e) => setSharedFilters(prev => ({ ...prev, name: e.target.value }))} placeholder="예: 방콕" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">공급사</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.supplier} onChange={(e) => setSharedFilters(prev => ({ ...prev, supplier: e.target.value }))} placeholder="예: 교원투어" />
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">종류</label>
                <input type="text" className="w-full border rounded px-2 py-1 text-sm" value={sharedFilters.kind} onChange={(e) => setSharedFilters(prev => ({ ...prev, kind: e.target.value }))} placeholder="예: 호텔" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border rounded-md max-h-80 overflow-y-auto">
                {sharedLoading ? (
                  <div className="p-4 text-sm text-gray-500">로딩 중...</div>
                ) : (!filteredSharedList || filteredSharedList.length === 0) ? (
                  <div className="p-4 text-sm text-gray-500">검색 결과가 없습니다.</div>
                ) : (
                  <ul className="divide-y">
                    {filteredSharedList.map((row, idx) => (
                      <li key={idx} className="p-2 text-[12px] flex items-center gap-2">
                        <input
                          type="radio"
                          name="shared-series-modal"
                          className="mt-0.5"
                          checked={String(sharedSelectTemp ?? selectedSeriesNo ?? '') === String(row?.시리즈번호)}
                          onChange={async () => {
                            setSharedSelectTemp(row?.시리즈번호);
                            await querySeatStatusForSeries(row?.시리즈번호, row?.시리즈명, row?.공유색상);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 truncate">{row?.시리즈명}</span>
                           
                          </div>
                          <div className="text-gray-500 truncate">#{row?.시리즈번호} · {row?.종류} · {row?.공급사}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border rounded-md max-h-80 overflow-y-auto p-2">
                {seatStatusLoading ? (
                  <div className="p-2 text-sm text-gray-500">좌석현황 불러오는 중...</div>
                ) : (seatStatus && seatStatus.length > 0) ? (
                  <div className="space-y-2 text-[12px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{seatStatusMeta.seriesName}</span>
                      
                      <span className="text-gray-500">#{seatStatusMeta.seriesNo}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-1">
                      {seatStatus.map((d, i) => {
                        const departDate = String(d?.['출발일'] ?? '');
                        const weekday = String(d?.['요일'] ?? '');
                        const block = String(d?.['블럭수'] ?? '');
                        const sales = String(d?.['판매인원'] ?? '');
                        const fare = String(d?.['항공료'] ?? '');
                        return (
                          <div key={i} className="px-2 py-1 grid items-center" style={{ gridTemplateColumns: '110px repeat(4, 1fr)' }}>
                            <div className="truncate text-[12px] font-semibold text-gray-800 pr-2">{departDate}</div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">요일: <b>{weekday || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">블럭수: <b>{block || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700 pr-2">판매인원: <b>{sales || '-'}</b></div>
                            <div className="text-right text-[11px] text-gray-700">항공료: <b>{fare || '-'}</b></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : seatStatusQueried ? (
                  <div className="p-2 text-sm text-gray-500">좌석이 없습니다.</div>
                ) : (
                  <div className="p-2 text-sm text-gray-500 text-center">우측에서 공유재고를 선택한 뒤 좌석현황을 확인해주세요.</div>
                )}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50" onClick={handleCloseSharedModal}>취소</button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={!sharedSelectTemp && !selectedSeriesNo}
              onClick={() => {
                const chosen = sharedSelectTemp ?? selectedSeriesNo ?? null;
                setSelectedSeriesNo(chosen);
                if (chosen) {
                  const found = (Array.isArray(sharedList) ? sharedList : []).find(row => String(row?.시리즈번호) === String(chosen));
                  setSelectedSeriesName(found?.시리즈명 || '');
                  setStockCount('');
                }
                setSharedModalOpen(false);
              }}
            >선택</button>
          </div>
        </div>
      </div>
    </div>, document.body));

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${animOut ? 'opacity-0' : 'opacity-100'} transition-opacity`} onClick={requestClose}>
      <div className="fixed inset-0 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <div className={`w-[96vw] max-w-6xl max-h-[98vh] bg-white shadow-2xl rounded-xl transform ${animOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'} transition-all`}>
          <div className="h-full max-h-[98vh] flex flex-col">
            <header className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">스마트 가격 등록</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={requestClose} aria-label="close">✕</button>
            </header>
            <main className="flex-1 p-5 overflow-hidden">
              <div className="flex flex-col gap-4 h-full min-h-0">
                <section className="space-y-2">
                  <div className="font-medium text-sm">입력 방식</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={`px-3 py-1.5 text-sm rounded border ${inputTab === 'json' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => { setInputTab('json'); setJsonEditorMounted(true); }}
                    >JSON (스마트 에디터)</button>
                    <button
                      type="button"
                      className={`px-3 py-1.5 text-sm rounded border ${inputTab === 'text' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => setInputTab('text')}
                    >텍스트</button>
                  </div>
                </section>

                <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-1 gap-5 flex-1 min-h-0">
                  <div className="min-h-0 h-full flex flex-col gap-4">
                    <section className="space-y-2 flex-1 min-h-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">붙여넣기</div>
                        {inputTab === 'text' && (
                          <div className="flex gap-4 text-sm">
                            <label className="inline-flex items-center gap-2">
                              <input type="radio" name="mode" value="total" checked={mode === 'total'} onChange={() => setMode('total')} />
                              합계요금 동일
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input type="radio" name="mode" value="byAge" checked={mode === 'byAge'} onChange={() => setMode('byAge')} />
                              연령별 컬럼
                            </label>
                          </div>
                        )}
                      </div>

                      <div style={{ display: inputTab === 'text' ? 'block' : 'none' }} className="h-full">
                        <textarea className="w-full h-full min-h-[220px] border rounded p-2 text-sm" placeholder={mode === 'total' ? '예)\n2025-10-01: 10000\n2025-10-02: 12000' : '예) 2025-10-01, 10000, 8000, 0'} value={text} onChange={(e) => setText(e.target.value)} />
                      </div>
                      <div style={{ display: inputTab === 'json' ? 'block' : 'none' }} className="h-full flex flex-col min-h-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="text-xs text-gray-500">한글, 워드, 엑셀 일정표의 가격관련 내용/표를 붙여넣고 전환을 실행하세요.</div>
                          <button
                            type="button"
                            className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleConvertFromSmartEditor}
                            disabled={convertLoading || parsing}
                          >{convertLoading ? '전환중...' : '일정표 전환'}</button>
                        </div>
                        <div className="border rounded overflow-hidden flex-1 min-h-[55vh]">
                          {jsonEditorMounted ? (
                            <iframe
                              ref={iframeRef}
                              title="SmartEditor"
                              src="/smarteditor/SmartEditor4.html?hideToolbar=1"
                              className="w-full h-full min-h-[55vh]"
                              style={{ border: 0 }}
                              onLoad={() => setSeReady(false)}
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-sm text-gray-500 bg-gray-50">에디터 로딩중...</div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="min-h-0 h-full flex flex-col gap-4">
                    {error && (
                      <div className="text-sm text-red-600">{error}</div>
                    )}

                    <section className="min-h-0">
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="font-medium text-sm">미리보기 ({rows.length}건)</div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">적용 요일</span>
                          {[
                            { k: 0, label: '일' },
                            { k: 1, label: '월' },
                            { k: 2, label: '화' },
                            { k: 3, label: '수' },
                            { k: 4, label: '목' },
                            { k: 5, label: '금' },
                            { k: 6, label: '토' },
                          ].map(({ k, label }) => {
                            const active = weekdaySelection.has(k);
                            return (
                              <button
                                key={k}
                                type="button"
                                className={`px-2 py-1 rounded border text-xs ${active ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                onClick={() => toggleWeekday(k)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="max-h-[50vh] overflow-auto border rounded">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-center w-10"> </th>
                              <th className="px-3 py-2 text-right">날짜</th>
                              <th className="px-3 py-2 text-right">성인</th>
                              <th className="px-3 py-2 text-right">소아</th>
                              <th className="px-3 py-2 text-right">유아</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => (
                              <tr key={i} className="odd:bg-white even:bg-gray-50">
                                <td className="px-2 py-1 text-center align-middle">
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-red-500 transition"
                                    onClick={() => removeRow(r.date)}
                                    aria-label="행 삭제"
                                    title="이 날짜 제거"
                                  >
                                    ×
                                  </button>
                                </td>
                                <td className="px-3 py-1 align-middle whitespace-nowrap">{formatDateWithWeekday(r.date)}</td>
                                <td className="px-3 py-1 text-right">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-24 text-right border rounded px-2 py-1 text-sm"
                                    value={formatNumberDisplay(r.adult)}
                                    onChange={(e) => updateRowValue(r.date, 'adult', e.target.value)}
                                  />
                                </td>
                                <td className="px-3 py-1 text-right">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-24 text-right border rounded px-2 py-1 text-sm"
                                    value={formatNumberDisplay(r.child)}
                                    onChange={(e) => updateRowValue(r.date, 'child', e.target.value)}
                                  />
                                </td>
                                <td className="px-3 py-1 text-right">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-24 text-right border rounded px-2 py-1 text-sm"
                                    value={formatNumberDisplay(r.infant)}
                                    onChange={(e) => updateRowValue(r.date, 'infant', e.target.value)}
                                  />
                                </td>
                              </tr>
                            ))}
                            {rows.length === 0 && (
                              <tr><td className="px-3 py-3 text-center text-gray-500" colSpan={5}>표시할 데이터가 없습니다.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">기본 재고 (선택)</div>
                        {enableSharedStock && (
                          <label className="flex items-center gap-2 text-xs font-semibold text-purple-700">
                            <input
                              type="checkbox"
                              className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                              checked={useSharedStock}
                              onChange={(e) => {
                                const next = !!e.target.checked;
                                if (!next) {
                                  setSelectedSeriesNo(null);
                                  setSelectedSeriesName('');
                                  setSharedSelectTemp(null);
                                  setUseSharedStock(false);
                                  return;
                                }
                                if (!selectedSeriesNo) {
                                  setSharedSelectTemp(null);
                                  setSharedModalOpen(true);
                                  return;
                                }
                                setStockCount('');
                                setUseSharedStock(true);
                              }}
                            />
                            <span>공유재고 사용</span>
                          </label>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          className={`w-40 border rounded px-2 py-1 text-sm text-right ${useSharedStock ? 'bg-gray-100 opacity-60' : ''}`}
                          placeholder="예: 20"
                          value={stockCount}
                          onChange={(e) => setStockCount(String(e.target.value).replace(/[^0-9]/g, ''))}
                          disabled={useSharedStock}
                        />
                        <span className="text-xs text-gray-500">입력 시, 적용된 모든 날짜에 동일 재고를 일괄 등록</span>
                      </div>
                      {enableSharedStock && selectedSeriesName && (
                        <div className="text-[11px] text-purple-700 font-semibold">
                          선택된 공유재고: <span className="text-purple-800">{selectedSeriesName}</span>
                        </div>
                      )}
                    </section>

                    <section className="space-y-2">
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-2.5">
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                            <input
                              type="checkbox"
                              className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                              checked={!!showCommission}
                              onChange={(e) => setShowCommission(!!e.target.checked)}
                            />
                            <span className="flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              판매 커미션 설정
                            </span>
                          </label>
                          {showCommission && (
                            <div className="flex items-center gap-1.5 bg-white/60 rounded-md px-2 py-0.5">
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name="bulk-import-commissionType"
                                  value="정률"
                                  checked={commissionType === '정률'}
                                  onChange={(e) => setCommissionType(e.target.value)}
                                  className="w-3 h-3 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-[10px] text-gray-600">정률(%)</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name="bulk-import-commissionType"
                                  value="정액"
                                  checked={commissionType === '정액'}
                                  onChange={(e) => setCommissionType(e.target.value)}
                                  className="w-3 h-3 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-[10px] text-gray-600">정액</span>
                              </label>
                            </div>
                          )}
                        </div>
                        {showCommission && (
                          <div className="space-y-1.5">
                            {mode === 'total' && inputTab !== 'json' ? (
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-emerald-600 font-medium w-10">합계</span>
                                <div className="flex-1 relative">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1.5 bg-white pr-8"
                                    value={commission.ma}
                                    onChange={(e) => {
                                      const v = formatCommission(e.target.value);
                                      setCommission({ ma: v, mc: v, mi: v });
                                    }}
                                    placeholder="0"
                                    aria-label="커미션 합계"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-emerald-500 font-medium">
                                    {commissionType === '정률' ? '%' : '￦'}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 gap-1.5">
                                <div>
                                  <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">성인</span>
                                  <div className="relative">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                                    value={commission.ma}
                                    onChange={(e) => setCommission((prev) => ({ ...prev, ma: formatCommission(e.target.value) }))}
                                    placeholder="0"
                                    aria-label="성인 커미션"
                                  />
                                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                                      {commissionType === '정률' ? '%' : '￦'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">소아</span>
                                <div className="relative">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                                    value={commission.mc}
                                    onChange={(e) => setCommission((prev) => ({ ...prev, mc: formatCommission(e.target.value) }))}
                                    placeholder="0"
                                    aria-label="소아 커미션"
                                  />
                                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                                      {commissionType === '정률' ? '%' : '￦'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span className="text-[10px] text-emerald-600 font-medium block mb-0.5 text-left">유아</span>
                                <div className="relative">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full border border-emerald-200 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition text-right text-xs px-2 py-1 bg-white pr-6"
                                    value={commission.mi}
                                    onChange={(e) => setCommission((prev) => ({ ...prev, mi: formatCommission(e.target.value) }))}
                                    placeholder="0"
                                    aria-label="유아 커미션"
                                  />
                                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500">
                                      {commissionType === '정률' ? '%' : '￦'}
                                  </span>
                                </div>
                              </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </main>
            <footer className="px-5 py-4 border-t flex justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>0원 행: {
                  rows.filter(r => (mode === 'total' ? Number(r.adult||0) === 0 : Number(r.adult||0) === 0 && Number(r.child||0) === 0 && Number(r.infant||0) === 0)).length
                }건</span>
              </div>
              <div className="flex gap-2">
                {onDeleteZero && (
                  <button className="px-4 py-2 rounded border border-red-300 text-red-600 hover:bg-red-50" onClick={handleDeleteZero} disabled={!rows.length}>0원 일괄 삭제</button>
                )}
                <button className="px-4 py-2 rounded border" onClick={requestClose}>취소</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={handleApply} disabled={!rows.length}>일괄 적용</button>
              </div>
            </footer>
            {sharedCreateModal}
            {sharedModal}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPriceImportModal;
